// src/modules/PeriodicReporting/utils/schemaLoader.js
// Loads and processes FI's AML JSON schema and mapping file for schema-driven form rendering.

import schema from '../data/aml_schema.json';
import mapping from '../data/aml_mapping.json';

// Map table IDs to translation key suffixes (used by t() calls in the form)
const TABLE_LABEL_KEYS = {
  'A.01.01': 'sectionA0101',
  'A.01.02': 'sectionA0102',
  'A.02.01': 'sectionA0201',
  'A.03.01': 'sectionA0301',
  'A.04.01': 'sectionA0401',
  'A.05.01': 'sectionA0501',
  'A.06.01': 'sectionA0601',
  'A.07.01': 'sectionA0701',
  'A.07.02': 'sectionA0702',
  'A.08.01': 'sectionA0801',
};

// Group headings for A.03.01 — maps first row_id in each group to a translation key suffix.
// Derived from FI's Excel template (AML_v4.0_20251231.xlsx), sheet "C. Kundkännedom".
const A0301_ROW_GROUP_HEADINGS = {
  '010': 'groupHeadingA0301_010',
  '030': 'groupHeadingA0301_030',
  '050': 'groupHeadingA0301_050',
  '070': 'groupHeadingA0301_070',
  '090': 'groupHeadingA0301_090',
  '150': 'groupHeadingA0301_150',
  '160': 'groupHeadingA0301_160',
  '180': 'groupHeadingA0301_180',
  '210': 'groupHeadingA0301_210',
};

// DYN-ROWS schema keys → report_table
const DYN_KEY_TO_TABLE = {
  'DYN-ROWS-A_01_02': 'A.01.02',
  'DYN-ROWS-A_07_02': 'A.07.02',
  'DYN-ROWS-A_08_01': 'A.08.01',
};

/**
 * Parse a clean label from FI's description format.
 * e.g., "Hur många verkliga huvudmän...? [A1] - Värde (010 - 010)" → "Hur många verkliga huvudmän...?"
 * e.g., "Antal kunder [A44]\nAnge i närmaste hundratal (020)" → "Antal kunder"
 */
function parseLabel(desc) {
  if (!desc) return '';
  let s = desc.trim();
  // Take first line (before any newline)
  const nlIdx = s.indexOf('\n');
  if (nlIdx > 0) s = s.slice(0, nlIdx).trim();
  // Strip the "[XN]" FI code suffix
  const bracketIdx = s.lastIndexOf(' [');
  if (bracketIdx > 0) s = s.slice(0, bracketIdx).trim();
  return s;
}

/**
 * Extract the FI data point identifier from a description.
 * e.g., "Antal kunder [A44]\nAnge..." → "A44"
 * e.g., "Hur många...? [A1] - Värde..." → "A1"
 * Returns null if no FI ID found.
 */
function parseFiId(desc) {
  if (!desc) return null;
  const match = desc.match(/\[([A-Z]\d+)\]/);
  return match ? match[1] : null;
}

/**
 * Determine UI field type from a JSON Schema data point definition.
 */
function getFieldType(dpDef) {
  const { type, format, enum: enumVals, multiOption, items, maxLength } = dpDef;
  if (type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'array' && multiOption && items && items.enum) return 'multi-select';
  if (type === 'string') {
    if (format === 'date') return 'date';
    if (enumVals) return 'select';
    if ((maxLength || 0) > 500) return 'textarea';
    return 'text';
  }
  return 'text';
}

/**
 * Build a metadata object for a single data point.
 * @param {string} dpId - Data point ID
 * @param {object} dpDef - Schema definition for this data point
 * @param {string} [tableId] - The report table this data point belongs to
 * @param {string} [rowId] - The row_id from the mapping (e.g. "010")
 */
function buildDataPointMeta(dpId, dpDef, tableId, rowId) {
  let groupHeadingKey = null;
  if (tableId === 'A.03.01' && rowId && A0301_ROW_GROUP_HEADINGS[rowId]) {
    groupHeadingKey = A0301_ROW_GROUP_HEADINGS[rowId];
  }
  return {
    id: dpId,
    label: parseLabel(dpDef.description || dpId),
    fiId: parseFiId(dpDef.description || ''),
    fieldType: getFieldType(dpDef),
    // For single-select (enum on dp) or multi-select (enum on items)
    enum: dpDef.enum || (dpDef.items && dpDef.items.enum) || null,
    min: dpDef.minimum,
    max: dpDef.maximum,
    maxLength: dpDef.maxLength,
    format: dpDef.format || null,
    groupHeadingKey,
  };
}

// Caches to avoid recomputing on every render
let _sectionsCache = null;
let _dynSectionsCache = null;

/**
 * Returns static sections: data points from $.data.* grouped by report_table,
 * in the order they appear in the mapping file.
 * Each section: { tableId, labelKey, dataPoints: [{ id, label, fieldType, enum, min, max, maxLength, format }] }
 */
export function getSections() {
  if (_sectionsCache) return _sectionsCache;

  const dataProps = schema.properties.data.properties;
  // tableId → [{dpId, rowId}] (preserving mapping order)
  const tableMap = new Map();

  for (const item of mapping.mapping) {
    if (item.path.startsWith('$.DYN-ROWS')) continue; // handled separately
    const { report_table: tableId, data_point_id: dpId, row_id: rowId } = item;
    if (!tableMap.has(tableId)) tableMap.set(tableId, []);
    const list = tableMap.get(tableId);
    if (!list.find(x => x.dpId === dpId)) list.push({ dpId, rowId });
  }

  const sections = [];
  for (const [tableId, items] of tableMap) {
    const dataPoints = items
      .filter(({ dpId }) => dataProps[dpId])
      .map(({ dpId, rowId }) => buildDataPointMeta(dpId, dataProps[dpId], tableId, rowId));
    if (dataPoints.length > 0) {
      sections.push({
        tableId,
        labelKey: TABLE_LABEL_KEYS[tableId] || tableId,
        dataPoints,
      });
    }
  }

  _sectionsCache = sections;
  return sections;
}

/**
 * Returns dynamic row sections (DYN-ROWS-*).
 * Each section: { tableId, labelKey, dynKey, dataPoints: [...] }
 * Data for these sections is stored in fields[dynKey] as an array of row objects.
 */
export function getDynSections() {
  if (_dynSectionsCache) return _dynSectionsCache;

  const sections = [];
  for (const [dynKey, tableId] of Object.entries(DYN_KEY_TO_TABLE)) {
    const dynSchema = schema.properties[dynKey];
    if (!dynSchema) continue;
    const itemProps = dynSchema.items && dynSchema.items.properties;
    if (!itemProps) continue;

    const dataPoints = Object.entries(itemProps).map(([dpId, dpDef]) =>
      buildDataPointMeta(dpId, dpDef)
    );

    sections.push({
      tableId,
      labelKey: TABLE_LABEL_KEYS[tableId] || tableId,
      dynKey,
      dataPoints,
    });
  }

  _dynSectionsCache = sections;
  return sections;
}

/** Returns the schema version tag (e.g. "4.0") */
export function getSchemaVersion() {
  return mapping.versionTag || '4.0';
}

/** Returns the schema $id URI */
export function getSchemaRef() {
  return schema.$id || '';
}

/** Returns a Set of required data point IDs (from schema.properties.data.required) */
let _requiredFieldsCache = null;
export function getRequiredFields() {
  if (_requiredFieldsCache) return _requiredFieldsCache;
  const required = schema.properties.data.required || [];
  _requiredFieldsCache = new Set(required);
  return _requiredFieldsCache;
}

/** Returns a Map of data point ID → human-readable label (parsed from schema descriptions) */
let _fieldLabelsCache = null;
export function getFieldLabels() {
  if (_fieldLabelsCache) return _fieldLabelsCache;
  const sections = getSections();
  _fieldLabelsCache = new Map();
  for (const section of sections) {
    for (const dp of section.dataPoints) {
      _fieldLabelsCache.set(dp.id, dp.label);
    }
  }
  return _fieldLabelsCache;
}
